import React, { useState, useEffect, useMemo } from "react";

import { Check } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

import Button from "../../../Components/Button";
import CommonInput from "../../../Components/Input";
import CommonModal from "../../../Components/Modal";
import Select from "../../../Components/Select";
import StepProgressBar from "../../../Components/StepProgressBar";
import Table from "../../../Components/Table";
import { formatPublishedAlignmentLevel } from "../../../Pages/Relationship/account-relationships/relationshipUtils";
import { useAppDispatch, useAppSelector } from "../../../Store/Hooks";
import {
  fetchAdminUsers,
  type AdminUser,
} from "../../../Store/Reducers/MasterSlice";
import {
  deleteAccountsBulk,
  clearDeleteAccountsBulkError,
  clearDeleteAccountsBulkSuccess,
  ActiveCustomerWithSponsorsItem,
  BulkDeleteAccountsPayload,
} from "../../../Store/Reducers/OpsSlice";
import { useAccountTiers } from "../../../Utils/Helper/accountTierHelpers";
import { formatCompactCurrency } from "../../../Utils/Helper/formatCompactCurrency";
import { formatDateTime } from "../../../Utils/Helper/Helper";
import {
  getFromLocalStorage,
  STORAGE_KEYS,
} from "../../../Utils/Helper/LocalStorage";

/** API may return role as a string or `{ name }` object. */
const resolveAdminUserRoleName = (user: AdminUser): string => {
  const { role, position } = user;
  if (typeof role === "string" && role.trim()) return role;
  if (role && typeof role === "object" && role.name) return role.name;
  return position || "";
};

interface DeleteAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  account: ActiveCustomerWithSponsorsItem | null;
  onSuccess?: () => void;
}

const DeleteAccountModal: React.FC<DeleteAccountModalProps> = ({
  isOpen,
  onClose,
  account,
  onSuccess,
}) => {
  const { formatTier } = useAccountTiers();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { orgSlug } = useParams<{ orgSlug?: string }>();
  const { loading: deleteLoading } = useAppSelector(
    state => state.ops.deleteAccountsBulk
  );
  const { items: adminUsers, loading: adminUsersLoading } = useAppSelector(
    state => state.master.adminUsers
  );

  const [step, setStep] = useState<1 | 2>(1);
  const [batchName, setBatchName] = useState("");
  const [selectedApprover, setSelectedApprover] = useState<string>("");

  // Get user ID from localStorage
  const loggedInUser = getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER);
  const userId = loggedInUser?.userId;

  // Transform admin users to approver options
  const approverOptions = useMemo(() => {
    return adminUsers.map(user => {
      const roleName = resolveAdminUserRoleName(user);
      const roleDisplay =
        roleName.charAt(0).toUpperCase() + roleName.slice(1).replace(/_/g, " ");

      return {
        label: `${user.full_name} - ${roleDisplay.toUpperCase()} (${roleDisplay})`,
        value: user.user_id,
      };
    });
  }, [adminUsers]);

  // Initialize batch name and fetch admin users when modal opens
  useEffect(() => {
    if (isOpen && account) {
      // Format batch name with timestamp
      const now = new Date();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const day = String(now.getDate()).padStart(2, "0");
      const year = now.getFullYear();
      const hours = String(now.getHours()).padStart(2, "0");
      const minutes = String(now.getMinutes()).padStart(2, "0");
      const seconds = String(now.getSeconds()).padStart(2, "0");
      const formattedBatchName = `Account Deletion - ${month}/${day}/${year}, ${hours}:${minutes}:${seconds}`;
      setBatchName(formattedBatchName);
      setStep(1);
      setSelectedApprover("");

      // Fetch admin users if not loaded
      if (adminUsers.length === 0 && !adminUsersLoading) {
        dispatch(fetchAdminUsers());
      }
    }
  }, [isOpen, account, adminUsers.length, adminUsersLoading, dispatch]);

  // Set default approver when options are available
  useEffect(() => {
    if (approverOptions.length > 0 && !selectedApprover && step === 1) {
      setSelectedApprover(approverOptions[0].value);
    }
  }, [approverOptions, selectedApprover, step]);

  // Prepare account data for table display
  const accountTableData = useMemo(() => {
    if (!account) return [];

    // Use API fields directly, with fallbacks
    const industry = account.industry || "N/A";
    const location =
      account.location ||
      account.city ||
      account.country ||
      account.region ||
      "N/A";

    return [
      {
        name: account.name || "N/A",
        region: account.region || "N/A",
        primarySponsors: account.primary_sponsors || "N/A",
        secondarySponsors: account.secondary_sponsors || "N/A",
        tiers: formatTier(account.tiers) || account.tiers || "N/A",
        industryLocation: `${industry} | ${location}`,
        alignmentLevel: formatPublishedAlignmentLevel(account.level),
        revenue: formatCompactCurrency(account.revenue, {
          fallback: "$0",
          zeroValue: "$0",
        }),
        renewal: account.renewal
          ? formatDateTime(account.renewal).date
          : account.last_level_date
            ? formatDateTime(account.last_level_date).date
            : "N/A",
      },
    ];
  }, [account, formatTier]);

  const handleConfirm = () => {
    if (selectedApprover) {
      setStep(2);
    }
  };

  const handleSubmit = async () => {
    if (!account || !selectedApprover || !userId) return;

    const payload: BulkDeleteAccountsPayload = {
      batch_name:
        batchName.trim() || `Account Deletion - ${new Date().toLocaleString()}`,
      approver_id: selectedApprover,
      customers: [
        {
          name: account.name || "",
          customer_id: account.customer_id,
        },
      ],
      upload_type: "delete_customer",
    };

    const result = await dispatch(deleteAccountsBulk(payload));

    if (result.meta.requestStatus === "fulfilled") {
      dispatch(clearDeleteAccountsBulkError());
      dispatch(clearDeleteAccountsBulkSuccess());
      // Reset state
      setStep(1);
      setBatchName("");
      setSelectedApprover("");
      onClose();
      // Call success callback to refresh accounts list
      if (onSuccess) {
        onSuccess();
      }
      // Ops lives under /:orgSlug for tenant users; IZBA admin uses root /ops
      const dataUploadHref = orgSlug
        ? `/${orgSlug}/ops?tab=data-upload`
        : "/ops?tab=data-upload";
      navigate(dataUploadHref);
    }
  };

  const handleClose = () => {
    dispatch(clearDeleteAccountsBulkError());
    dispatch(clearDeleteAccountsBulkSuccess());
    setStep(1);
    setBatchName("");
    setSelectedApprover("");
    onClose();
  };

  const selectedApproverDetails = useMemo(() => {
    if (!selectedApprover) return null;
    const approver = adminUsers.find(u => u.user_id === selectedApprover);
    if (!approver) return null;
    const roleName = resolveAdminUserRoleName(approver);
    // Use the role code in uppercase format (e.g., "C_LEVEL")
    const roleCode = roleName.toUpperCase().replace(/\s/g, "_");
    return {
      name: approver.full_name,
      role: roleCode,
    };
  }, [selectedApprover, adminUsers]);

  if (!account) return null;

  return (
    <CommonModal
      isOpen={isOpen}
      toggle={handleClose}
      title="Delete Account"
      size="4xl"
    >
      <div className="pb-2">
        {/* Step Indicator */}
        <div className="mb-8">
          <p className="text-sm font-medium text-gray-700 mb-3">
            {step === 1
              ? "Step 1 of 2: Review & Select Approver"
              : "Step 2 of 2: Confirm Submission"}
          </p>
          <StepProgressBar
            steps={[
              { label: "Review & Select Approver" },
              { label: "Confirm Submission" },
            ]}
            currentStepIndex={step}
          />
        </div>

        {/* Step 1: Review & Select Approver */}
        {step === 1 && (
          <div className="space-y-6">
            {/* Account Information Section */}
            <div>
              <h5 className="text-base font-semibold text-gray-900 mb-4">
                Delete profile information for &apos;{account.name}&apos;
              </h5>
              <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                <Table
                  columns={[
                    { key: "name", title: "NAME" },
                    { key: "region", title: "REGION" },
                    { key: "industryLocation", title: "INDUSTRY & LOCATION" },
                    { key: "primarySponsors", title: "PRIMARY SPONSORS" },
                    { key: "secondarySponsors", title: "SECONDARY SPONSORS" },
                    { key: "tiers", title: "TIERS" },
                    { key: "alignmentLevel", title: "ACCOUNT LEVEL" },
                    { key: "revenue", title: "REVENUE" },
                    { key: "renewal", title: "RENEWAL" },
                  ]}
                  data={accountTableData}
                />
              </div>
            </div>

            {/* Approval Required Section */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-5 shadow-sm">
              <div className="font-bold text-base text-gray-900 mb-5">
                Approval Required
              </div>

              <div className="space-y-4">
                <CommonInput
                  name="batchName"
                  value={batchName}
                  onChange={e => setBatchName(e.target.value)}
                  label="Batch Name (Optional)"
                  placeholder="Enter batch name"
                  className="mb-8"
                />

                {adminUsersLoading ? (
                  <div className="text-gray-600 text-sm py-2">
                    Loading approvers...
                  </div>
                ) : adminUsersLoading === false &&
                  approverOptions.length === 0 ? (
                  <div className="text-red-600 text-sm py-2">
                    No approvers available. Please contact an administrator.
                  </div>
                ) : (
                  <Select
                    name="approver"
                    value={selectedApprover}
                    options={approverOptions}
                    onChange={e => setSelectedApprover(String(e.target.value))}
                    isShowPlaceHolder={false}
                    required
                    disabled={adminUsersLoading || approverOptions.length === 0}
                    label="Select Approver (Admin, C-Level, or Executive Leader)"
                  />
                )}

                <div className="text-orange-600 text-sm font-medium mt-3 pt-2 border-t border-yellow-200">
                  This account will be deleted after approval
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Confirm Submission */}
        {step === 2 && (
          <div className="text-center py-8">
            <div className="space-y-4">
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4 shadow-sm">
                <Check className="w-10 h-10 text-green-600" />
              </div>
              <div className="font-bold text-xl text-gray-900 mb-2">
                Ready to Submit
              </div>
              <p className="text-gray-600 text-base max-w-md mx-auto leading-relaxed">
                You are about to submit changes for account &quot;{account.name}
                &quot; for approval.
              </p>
              {selectedApproverDetails && (
                <div className="mt-4 pt-4 border-gray-200">
                  <p className="text-gray-700 font-medium">
                    Approver:{" "}
                    <span className="text-gray-900">
                      {selectedApproverDetails.name}
                    </span>{" "}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Footer Actions */}
        <div className="flex justify-between items-center pt-6 border-t border-gray-200">
          {step === 1 && (
            <>
              <Button
                variant="outline-primary"
                className="btn-lg"
                onClick={handleClose}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                className="btn-lg"
                onClick={handleConfirm}
                disabled={!selectedApprover || adminUsersLoading}
              >
                Confirm
              </Button>
            </>
          )}
          {step === 2 && (
            <>
              <Button
                variant="outline-primary"
                className="btn-lg"
                onClick={() => setStep(1)}
                disabled={deleteLoading}
              >
                Back
              </Button>
              <Button
                variant="primary"
                className="btn-lg"
                onClick={handleSubmit}
                disabled={deleteLoading}
              >
                {deleteLoading ? "Submitting..." : "Submit for Approval"}
              </Button>
            </>
          )}
        </div>
      </div>
    </CommonModal>
  );
};

export default DeleteAccountModal;
